import React from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  MenuItem,
} from "@mui/material";
import { Formik, Form, FieldArray } from "formik";
import * as yup from "yup";
import { useTheme } from "@mui/material/styles";
import { tokens } from "../../../theme";
import { useAddDataAssetVersionsMutation } from "../../../features/data_assets/dataAssetsApiSlice";

/* ——— validation ——— */
const VersionSchema = yup.object().shape({
  versions: yup.array().of(
    yup.object().shape({
      major: yup.number().integer().min(0).required(),
      minor: yup.number().integer().min(0).required(),
      patch: yup.number().integer().min(0).required(),
      source: yup.string().oneOf(["Snowflake", "S3", "Both"]).required(),
      snowflake_table: yup.string().when("source", {
        is: (src) => src === "Snowflake" || src === "Both",
        then: (schema) => schema.required("Table required"),
        otherwise: (schema) => schema,
      }),
      s3_uri: yup.string().when("source", {
        is: (src) => src === "S3" || src === "Both",
        then: (schema) => schema.required("URI required"),
        otherwise: (schema) => schema,
      }),
    })
  ),
});

const AddVersionWidget = ({ asset, onSave, onClose }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [addVersions, { isLoading: apiLoading }] = useAddDataAssetVersionsMutation();

  return (
    <Formik
      initialValues={{
        versions: [
          { major: "", minor: "", patch: "", source: "", s3_uri: "", snowflake_table: "" },
        ],
      }}
      validationSchema={VersionSchema}
      onSubmit={async (values, { setSubmitting }) => {
        /* build formatted + timestamped versions */
        const nowIso = new Date().toISOString();
        const newVersions = values.versions.map((v) => ({
          ...v,
          version: `${v.major}.${v.minor}.${v.patch}`,
          timestamp_utc: nowIso,
        }));

        await addVersions({
          id: asset.id || asset._id,
          versions: newVersions,
        }).unwrap();

        /* call optional onSave callback so parent can refetch or toast */
        onSave?.();
        setSubmitting(false);
        onClose();
      }}
    >
      {({ values, setFieldValue, handleBlur, isSubmitting }) => (
        <Form>
          <Typography variant="h6" sx={{ mb: 2, color: colors.blueAccent[400] }}>
            Add new version for {asset.name}
          </Typography>

          <FieldArray name="versions">
            {({ push, remove }) => (
              <>
                {values.versions.map((_, idx) => (
                  <Box
                    key={idx}
                    display="flex"
                    alignItems="center"
                    mb={2}
                    sx={{ "& .MuiTextField-root": { mr: 1 } }}
                  >
                    {/* semver fields */}
                    {["major", "minor", "patch"].map((part, p) => (
                      <TextField
                        key={part}
                        variant="filled"
                        type="number"
                        size="small"
                        label={part.charAt(0).toUpperCase() + part.slice(1)}
                        onBlur={handleBlur}
                        value={values.versions[idx][part]}
                        onChange={(e) =>
                          setFieldValue(`versions.${idx}.${part}`, e.target.value)
                        }
                        inputProps={{ min: 0, style: { width: 60 } }}
                      />
                    ))}

                    {/* source select */}
                    <TextField
                      select
                      variant="filled"
                      size="small"
                      label="Source"
                      value={values.versions[idx].source}
                      onChange={(e) =>
                        setFieldValue(`versions.${idx}.source`, e.target.value)
                      }
                      sx={{ width: 130 }}
                    >
                      <MenuItem value="Snowflake">Snowflake</MenuItem>
                      <MenuItem value="S3">S3</MenuItem>
                      <MenuItem value="Both">Both</MenuItem>
                    </TextField>

                    {/* conditional fields */}
                    {(values.versions[idx].source === "Snowflake" ||
                      values.versions[idx].source === "Both") && (
                        <TextField
                          variant="filled"
                          size="small"
                          label="Snowflake Table"
                          value={values.versions[idx].snowflake_table}
                          onChange={(e) =>
                            setFieldValue(
                              `versions.${idx}.snowflake_table`,
                              e.target.value
                            )
                          }
                          sx={{ flex: 1 }}
                        />
                      )}
                    {(values.versions[idx].source === "S3" ||
                      values.versions[idx].source === "Both") && (
                        <TextField
                          variant="filled"
                          size="small"
                          label="S3 URI"
                          value={values.versions[idx].s3_uri}
                          onChange={(e) =>
                            setFieldValue(`versions.${idx}.s3_uri`, e.target.value)
                          }
                          sx={{ flex: 1 }}
                        />
                      )}

                    <Button
                      size="small"
                      color="secondary"
                      variant="outlined"
                      sx={{ ml: 1 }}
                      onClick={() => remove(idx)}
                    >
                      Delete
                    </Button>
                  </Box>
                ))}

                <Button
                  size="small"
                  variant="outlined"
                  color="secondary"
                  onClick={() =>
                    push({ major: "", minor: "", patch: "", source: "", s3_uri: "", snowflake_table: "" })
                  }
                >
                  +&nbsp;Add Version
                </Button>
              </>
            )}
          </FieldArray>

          <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              size="small"
              type="submit"
              variant="contained"
              disabled={isSubmitting}
              sx={{
                backgroundColor: colors.greenAccent[500],
                color: colors.grey[100],
                '&:hover': { backgroundColor: colors.greenAccent[400] }
              }}
            >
              Save
            </Button>
          </Box>
        </Form>
      )}
    </Formik>
  );
};

export default AddVersionWidget;
